import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { IntlProvider, type IntlConfig } from 'react-intl';
import * as Sentry from '@sentry/react';
import { DEFAULT_LOCALE, dictionaries, LocaleSchema, type Locale } from '@immo/i18n';
import { flattenMessages } from './flatten';

// Module-scoped dedupe so the same missing key reported on every render
// doesn't spam Sentry. Re-fires once per (locale, key) pair per session.
const reportedMissingKeys = new Set<string>();

type IntlError = Parameters<NonNullable<IntlConfig['onError']>>[0];

function reportIntlError(locale: Locale, err: IntlError): void {
  // react-intl exposes the failure mode via a string `code`. Missing
  // translations are the "shouldn't happen" path — the typed dictionary
  // + CI guard ought to prevent them, but if one slips through we want
  // a Sentry breadcrumb pointing at the specific key. Other intl errors
  // (formatter failures, malformed ICU) stay silent; ADR 0009 owns the
  // policy and the dictionary shape rules them out at build time.
  const code = (err as { code?: unknown }).code;

  if (code !== 'MISSING_TRANSLATION') {
    return;
  }

  const descriptor = (err as { descriptor?: { id?: unknown } }).descriptor;
  const id = typeof descriptor?.id === 'string' ? descriptor.id : '(unknown)';
  const dedupeKey = `${locale}::${id}`;

  if (reportedMissingKeys.has(dedupeKey)) {
    return;
  }

  reportedMissingKeys.add(dedupeKey);
  Sentry.captureMessage(`i18n missing translation: ${id}`, {
    level: 'warning',
    tags: { 'i18n.error': 'missing-translation', 'i18n.locale': locale, 'i18n.key': id },
  });
}

const flatDictionaries: Record<Locale, Record<string, string>> = {
  'de-DE': flattenMessages(dictionaries['de-DE']),
  'en-US': flattenMessages(dictionaries['en-US']),
};

const STORAGE_KEY = 'immoos.locale';

type LocaleContextValue = {
  locale: Locale;
  /**
   * Returns `true` if the value is a supported locale and was applied;
   * `false` otherwise (current locale untouched, console.warn fired).
   * The signature accepts `string` because the callers we care about
   * carrying invalid values come from JS-land (the browser console via
   * `window.setLocale`, a future settings UI sending a form field).
   */
  setLocale: (next: string) => boolean;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readPersistedLocale(): Locale {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (raw === null) {
      return DEFAULT_LOCALE;
    }

    const parsed = LocaleSchema.safeParse(raw);

    return parsed.success ? parsed.data : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

/**
 * Locale-aware i18n provider.
 *
 * NOTE: this deviates from [ADR 0009](../../../../.docs/engineering/adr/0009-i18n.md)'s
 * original "no persistence, hardcoded boot locale" stance. The user-facing
 * affordance still doesn't exist (no UI, no auto-detection, no URL
 * inference); the only seam to flip the locale is the same programmatic
 * `setLocale(...)` it always was. What changed: we now read a previous
 * `setLocale` choice back from `localStorage` on boot, so the engineer
 * console flow (`window.setLocale('en-US')`) survives a reload. The ADR
 * needs an amendment to record this — track in a follow-up.
 *
 * `setLocale` rejects unknown values: it leaves the current locale in
 * place, fires a `console.warn`, and returns `false`. Supported values
 * are the keys of `dictionaries` enforced by `LocaleSchema`.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readPersistedLocale);

  const setLocale = useCallback((next: string): boolean => {
    const parsed = LocaleSchema.safeParse(next);

    if (!parsed.success) {
      console.warn(`Ignoring setLocale(${JSON.stringify(next)}): not a supported locale`);

      return false;
    }

    setLocaleState(parsed.data);

    try {
      window.localStorage.setItem(STORAGE_KEY, parsed.data);
    } catch {
      // Private mode / quota: the locale still flips in-memory for the
      // current tab; only the persistence-across-refresh promise is
      // broken. Don't surface — the caller already got `true`.
    }

    return true;
  }, []);

  // Mirror the setter onto `window.setLocale` so engineers can flip the
  // active locale from the browser console — `window.setLocale('en-US')`
  // — in any build. Returns `false` when the argument isn't a supported
  // locale; persists successful flips to `localStorage` so a refresh
  // keeps the chosen language.
  useEffect(() => {
    window.setLocale = setLocale;
  }, [setLocale]);

  // Keep `<html lang>` in sync with the active locale. `index.html` ships
  // with the default; this effect runs after hydration and tracks any
  // subsequent setLocale, so screen readers, browser translate UI, and
  // CSS `:lang()` selectors all see the truth instead of the stale boot
  // value.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const contextValue = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

  // react-intl shallow-compares its config on every render and mints a NEW
  // `intl` whenever the comparison fails. An inline `onError` arrow fails it
  // every time, which invalidates every `useTranslations()` in the app on any
  // ancestor re-render — the identity churn behind IMM-563. Keyed on `locale`
  // so `intl` changes exactly when the locale genuinely does.
  const onIntlError = useCallback((err: IntlError) => reportIntlError(locale, err), [locale]);

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={flatDictionaries[locale]}
        onError={onIntlError}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);

  if (!ctx) throw new Error('useLocale must be used inside <I18nProvider>');

  return ctx;
}
